import React, { useMemo, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import '../Clubs/ClubsDashboard.css';
import logo from '../../../assets/images/logo.svg';
import { formatStandIdentity, parseStandIdentity } from './standClayConfig';

const BRAND_ORANGE = '#f27a00';
const BRAND_GREEN = '#88b04b';
const BRAND_DARK = '#171717';
const BRAND_MUTED = '#5f6368';

const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const wrapCanvasText = (ctx, text, x, y, maxWidth, lineHeight) => {
    if (!text) {
        return y;
    }

    const words = String(text).split(/\s+/).filter(Boolean);
    const lines = [];
    let currentLine = '';

    words.forEach((word) => {
        const nextLine = currentLine ? `${currentLine} ${word}` : word;
        if (ctx.measureText(nextLine).width > maxWidth && currentLine) {
            lines.push(currentLine);
            currentLine = word;
        } else {
            currentLine = nextLine;
        }
    });

    if (currentLine) {
        lines.push(currentLine);
    }

    lines.forEach((line, index) => {
        ctx.fillText(line, x, y + (index * lineHeight));
    });

    return y + (Math.max(lines.length, 1) * lineHeight);
};

const drawBrandMark = (ctx, width) => {
    const centerX = width / 2;
    const topY = 40;
    const iconX = centerX - 120;
    const iconY = topY + 16;

    ctx.save();
    ctx.lineWidth = 4;
    ctx.strokeStyle = BRAND_ORANGE;
    ctx.beginPath();
    ctx.arc(iconX, iconY, 18, 0, Math.PI * 2);
    ctx.stroke();

    ctx.fillStyle = BRAND_GREEN;
    ctx.beginPath();
    ctx.arc(iconX, iconY, 6, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = BRAND_ORANGE;
    ctx.beginPath();
    ctx.moveTo(iconX - 28, iconY);
    ctx.lineTo(iconX - 10, iconY);
    ctx.moveTo(iconX + 10, iconY);
    ctx.lineTo(iconX + 28, iconY);
    ctx.moveTo(iconX, iconY - 28);
    ctx.lineTo(iconX, iconY - 10);
    ctx.moveTo(iconX, iconY + 10);
    ctx.lineTo(iconX, iconY + 28);
    ctx.stroke();
    ctx.restore();

    ctx.textAlign = 'left';
    ctx.fillStyle = BRAND_MUTED;
    ctx.font = '600 24px Arial';
    ctx.fillText('SHOOT', centerX - 92, topY + 10);
    ctx.fillStyle = BRAND_DARK;
    ctx.font = 'bold 34px Arial';
    ctx.fillText('CONN', centerX - 92, topY + 42);
    ctx.fillStyle = BRAND_ORANGE;
    ctx.fillText('ECT', centerX - 8, topY + 42);
    ctx.textAlign = 'center';
};

const QRCodeModal = ({ stand, onClose }) => {
    const qrCodeRef = useRef(null);
    const standIdentity = useMemo(() => parseStandIdentity(stand?.name), [stand?.name]);
    const displayTitle = formatStandIdentity(standIdentity?.standNumber, standIdentity?.standTitle, stand?.name) || stand?.name || 'Stand';
    const description = stand?.description?.trim() || '';
    const standAccessCode = String(stand?.standAccessCode || '').trim();
    const qrUrl = standAccessCode ? `https://shootconnect.app/s/${standAccessCode}` : 'https://shootconnect.app/stand-access';
    const manualEntryUrl = 'https://shootconnect.app/stand-access';
    const metadata = [
        stand?.discipline ? `Discipline: ${stand.discipline}` : null,
        stand?.variant ? `Variant: ${stand.variant}` : null
    ].filter(Boolean);

    const buildPrintMarkup = () => {
        const metaMarkup = metadata.length > 0
            ? `<div class="qr-meta">${metadata.map((item) => `<span>${escapeHtml(item)}</span>`).join('')}</div>`
            : '';
        const descriptionMarkup = description
            ? `<p class="qr-description">${escapeHtml(description)}</p>`
            : '';
        const codeMarkup = standAccessCode
            ? `<div class="qr-manual-code">Stand Code: <strong>${escapeHtml(standAccessCode)}</strong></div>`
            : '';

        return `
            <html>
                <head>
                    <title>Shoot Connect - ${escapeHtml(displayTitle)}</title>
                    <style>
                        * { box-sizing: border-box; }
                        body {
                            margin: 0;
                            background: #f4f1ea;
                            font-family: Arial, sans-serif;
                            color: ${BRAND_DARK};
                            padding: 28px;
                        }
                        .print-card {
                            max-width: 760px;
                            margin: 0 auto;
                            background: #ffffff;
                            border: 2px solid #ece7df;
                            border-radius: 28px;
                            padding: 34px 40px 36px;
                            text-align: center;
                        }
                        .brand {
                            display: inline-flex;
                            align-items: center;
                            gap: 14px;
                            margin-bottom: 18px;
                        }
                        .brand-mark {
                            position: relative;
                            width: 38px;
                            height: 38px;
                            border: 3px solid ${BRAND_ORANGE};
                            border-radius: 50%;
                        }
                        .brand-mark::before,
                        .brand-mark::after {
                            content: '';
                            position: absolute;
                            background: ${BRAND_ORANGE};
                        }
                        .brand-mark::before {
                            width: 3px;
                            height: 52px;
                            top: -10px;
                            left: 50%;
                            transform: translateX(-50%);
                        }
                        .brand-mark::after {
                            width: 52px;
                            height: 3px;
                            top: 50%;
                            left: -10px;
                            transform: translateY(-50%);
                        }
                        .brand-mark-dot {
                            position: absolute;
                            width: 12px;
                            height: 12px;
                            background: ${BRAND_GREEN};
                            border-radius: 50%;
                            inset: 50% auto auto 50%;
                            transform: translate(-50%, -50%);
                        }
                        .brand-copy {
                            text-align: left;
                            line-height: 1;
                        }
                        .brand-copy .shoot {
                            display: block;
                            color: ${BRAND_MUTED};
                            font-size: 18px;
                            font-weight: 700;
                            letter-spacing: 0.12em;
                        }
                        .brand-copy .connect {
                            display: block;
                            font-size: 28px;
                            font-weight: 800;
                        }
                        .brand-copy .connect .accent {
                            color: ${BRAND_ORANGE};
                        }
                        h1 {
                            margin: 0;
                            font-size: 40px;
                            line-height: 1.1;
                        }
                        .qr-description {
                            margin: 18px auto 0;
                            max-width: 540px;
                            color: ${BRAND_MUTED};
                            font-size: 18px;
                            line-height: 1.5;
                        }
                        .qr-meta {
                            margin-top: 18px;
                            display: flex;
                            gap: 12px;
                            justify-content: center;
                            flex-wrap: wrap;
                        }
                        .qr-meta span {
                            border: 1px solid #e1d8cd;
                            border-radius: 999px;
                            padding: 10px 16px;
                            font-size: 15px;
                            font-weight: 700;
                            background: #fbf9f6;
                        }
                        .qr-code {
                            margin: 30px auto 24px;
                            display: flex;
                            justify-content: center;
                        }
                        .cta {
                            margin: 0;
                            color: ${BRAND_DARK};
                            font-size: 20px;
                            font-weight: 700;
                        }
                        .manual-url {
                            margin-top: 12px;
                            color: ${BRAND_MUTED};
                            font-size: 15px;
                        }
                        .qr-manual-code {
                            margin-top: 14px;
                            font-size: 18px;
                            color: ${BRAND_DARK};
                        }
                        .url {
                            margin-top: 10px;
                            color: ${BRAND_MUTED};
                            font-size: 16px;
                            word-break: break-word;
                        }
                        @media print {
                            body {
                                background: #ffffff;
                                padding: 0;
                            }
                            .print-card {
                                border: none;
                                max-width: none;
                                border-radius: 0;
                                padding: 26px 20px;
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="print-card">
                        <div class="brand">
                            <div class="brand-mark"><span class="brand-mark-dot"></span></div>
                            <div class="brand-copy">
                                <span class="shoot">SHOOT</span>
                                <span class="connect">CONN<span class="accent">ECT</span></span>
                            </div>
                        </div>
                        <h1>${escapeHtml(displayTitle)}</h1>
                        ${descriptionMarkup}
                        ${metaMarkup}
                        <div class="qr-code">${qrCodeRef.current?.innerHTML || ''}</div>
                        <p class="cta">Sign up to score today</p>
                        <div class="manual-url">Go to ${escapeHtml(manualEntryUrl)} and enter the stand code.</div>
                        ${codeMarkup}
                        <div class="url">${escapeHtml(qrUrl)}</div>
                    </div>
                </body>
            </html>
        `;
    };

    const handlePrint = () => {
        const printWindow = window.open('', '_blank', 'width=900,height=900');
        if (!printWindow) {
            return;
        }

        printWindow.document.open();
        printWindow.document.write(buildPrintMarkup());
        printWindow.document.close();

        printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
            printWindow.close();
        };
    };

    const handleDownload = () => {
        const svg = qrCodeRef.current?.querySelector('svg');
        if (!svg) {
            return;
        }

        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        canvas.width = 900;
        canvas.height = 1180;

        img.onload = () => {
            ctx.fillStyle = '#f4f1ea';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            ctx.fillStyle = '#ffffff';
            ctx.strokeStyle = '#ece7df';
            ctx.lineWidth = 4;
            const cardX = 48;
            const cardY = 38;
            const cardWidth = canvas.width - (cardX * 2);
            const cardHeight = canvas.height - (cardY * 2);
            ctx.beginPath();
            ctx.roundRect(cardX, cardY, cardWidth, cardHeight, 28);
            ctx.fill();
            ctx.stroke();

            drawBrandMark(ctx, canvas.width);

            ctx.fillStyle = BRAND_DARK;
            ctx.font = 'bold 50px Arial';
            ctx.textAlign = 'center';
            let currentY = 180;
            currentY = wrapCanvasText(ctx, displayTitle, canvas.width / 2, currentY, 640, 58);

            if (description) {
                currentY += 18;
                ctx.fillStyle = BRAND_MUTED;
                ctx.font = '24px Arial';
                currentY = wrapCanvasText(ctx, description, canvas.width / 2, currentY, 620, 34);
            }

            if (metadata.length > 0) {
                currentY += 34;
                ctx.font = 'bold 24px Arial';
                metadata.forEach((item) => {
                    ctx.fillStyle = '#fbf9f6';
                    ctx.strokeStyle = '#e1d8cd';
                    ctx.lineWidth = 2;
                    const pillWidth = Math.min(500, ctx.measureText(item).width + 56);
                    const pillX = (canvas.width - pillWidth) / 2;
                    const pillY = currentY - 24;
                    ctx.beginPath();
                    ctx.roundRect(pillX, pillY, pillWidth, 48, 24);
                    ctx.fill();
                    ctx.stroke();

                    ctx.fillStyle = BRAND_DARK;
                    ctx.fillText(item, canvas.width / 2, currentY + 8);
                    currentY += 68;
                });
            }

            const qrSize = 320;
            const qrY = currentY + 20;
            ctx.drawImage(img, (canvas.width - qrSize) / 2, qrY, qrSize, qrSize);

            ctx.fillStyle = BRAND_DARK;
            ctx.font = 'bold 30px Arial';
            ctx.fillText('Sign up to score today', canvas.width / 2, qrY + qrSize + 80);

            ctx.fillStyle = BRAND_MUTED;
            ctx.font = '22px Arial';
            let footerY = wrapCanvasText(ctx, `Go to ${manualEntryUrl} and enter the stand code.`, canvas.width / 2, qrY + qrSize + 124, 620, 30);

            if (standAccessCode) {
                footerY += 24;
                ctx.fillStyle = BRAND_DARK;
                ctx.font = 'bold 34px Arial';
                ctx.fillText(`Stand Code: ${standAccessCode}`, canvas.width / 2, footerY);
                footerY += 42;
            }

            ctx.fillStyle = BRAND_MUTED;
            ctx.font = '18px Arial';
            wrapCanvasText(ctx, qrUrl, canvas.width / 2, footerY, 620, 26);

            const link = document.createElement('a');
            link.download = `ShootConnect-${displayTitle.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || 'stand'}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        };

        img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
    };

    return (
        <div className="qr-modal">
            <div className="qr-modal-content">
                <button className="close-button" onClick={onClose}>&times;</button>

                <div className="qr-branded-container">
                    <img
                        src={logo}
                        alt="Shoot Connect Logo"
                        className="qr-logo"
                    />

                    <h2>{displayTitle}</h2>

                    {description ? (
                        <p className="stand-info" style={{ maxWidth: '460px', color: '#5f6368', marginBottom: '10px' }}>
                            {description}
                        </p>
                    ) : null}

                    {metadata.length > 0 ? (
                        <div className="stand-info">
                            {metadata.map((item) => (
                                <p key={item}>{item}</p>
                            ))}
                        </div>
                    ) : null}

                    <div className="qr-code-container" ref={qrCodeRef}>
                        <QRCodeSVG
                            value={qrUrl}
                            size={256}
                            level="H"
                            includeMargin={true}
                        />
                    </div>

                    <p style={{ margin: '0 0 6px', fontWeight: 700, color: '#171717' }}>Sign up to score today</p>
                    <p className="stand-info" style={{ margin: '0 0 8px', color: '#5f6368' }}>
                        Go to <strong>{manualEntryUrl}</strong> and enter the stand code.
                    </p>
                    {standAccessCode ? (
                        <div className="website-url" style={{ fontWeight: 800, color: '#171717' }}>
                            Stand Code: {standAccessCode}
                        </div>
                    ) : null}
                    <div className="website-url" style={{ fontSize: '0.92rem' }}>{qrUrl}</div>

                    <div className="qr-actions">
                        <button
                            onClick={handlePrint}
                            className="dashboard-card-default-btn"
                        >
                            Print QR Code
                        </button>
                        <button
                            onClick={handleDownload}
                            className="dashboard-card-default-btn ml-3"
                        >
                            Download QR Code
                        </button>
                        <button
                            onClick={onClose}
                            className="dashboard-card-outline-btn ml-3"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QRCodeModal;
